import { Dialog } from 'primereact/dialog'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHref } from 'react-router-dom'
import { GRANTS } from '../../../auth/grants'
import { useGrants } from '../../../auth/useCurrentUser'
import MainTaskDetailView from '../../actions/components/MainTaskDetailView'
import SubTaskDetailView from '../../actions/components/SubTaskDetailView'
import '../../actions/actions.css'

/** Pop-up'ın açık olduğu görev — satırın kendisi değil asgari şekli (bkz. sayfadaki `filesTarget`). */
export interface TaskDetailTarget {
  id: number
  subTask: boolean
}

interface Props {
  /** `null` → kapalı. */
  task: TaskDetailTarget | null
  onHide: () => void
}

/**
 * Toplantı Yönetimi <b>görev detay</b> pop-up'ı (kullanıcı isteği 02.10.2026) — satıra tıklanınca
 * açılır; ana görevde {@link MainTaskDetailView}, alt görevde {@link SubTaskDetailView} çizer. İkisi
 * de salt-okumadır: bu ekran toplu karar ekranıdır, görev düzenleme Görev Detay sayfasında kalır.
 *
 * <p><b>Ana görevin alt görev listesi tıklanabilir</b> ve alt görevi <b>yeni sekmede</b> açar
 * (kullanıcı isteği). Yeni sekme bilinçlidir: aynı sekmede gezinmek pop-up'ı, süzgeçleri ve
 * seçimi (toplu kapatma/yeniden açma) kaybettirirdi. Hedef Alt Görev Detay sayfasıdır
 * ({@code /minutes/subactions/:id}); rota {@code SUBACTION_PAGE_VIEW} ister, yetkisi olmayan
 * kullanıcıda satırlar tıklanamaz kalır — tıklanabilir görünüp yetki ekranına düşen bir bağlantı
 * çizilmez. Router {@code basename}'i {@code useHref} ile çözülür.
 *
 * <p>Veri kapsamı: detay ucu {@code ActionViewScope} ile korunur; bu ekranın listesi de aynı
 * kapsamı ({@code ACTION_VISIBLE}) uygular, yani listede görünen her satır <b>kapsam açısından</b>
 * açılabilir. <b>Yetki aynı değildir:</b> detay ucu ekranın yetkisine ek olarak bir görev okuma
 * yetkisi ({@code HAS_ACTION_READ_ANY}) ister; yalnız Toplantı Yönetimi yetkisi olan bir rol burada
 * genel hata metnini görür (bilinçli sınır — spec 105, Clarifications 2026-10-02).
 *
 * <p><b>Kapanışta son görev tutulur</b> (MR !284 Duo bulgusu): {@code Dialog} kapanış geçişi
 * bitene kadar içeriği çizmeye devam eder, oysa sayfa {@code task}'ı hemen {@code null} yapar.
 * Başlık ve gövde {@code task}'tan türeseydi alt görev pop-up'ı kapanırken başlık bir anlığına
 * "Ana Görev Detayı"na döner ve gövde sönmeden kaybolurdu. Bu yüzden ikisi de son gösterilen
 * görevden ({@code shown}) türer. Gövdeye {@code active} geçilmez: kapalı {@code Dialog} hiçbir şey
 * çizmez, yani kapalı pop-up yine ağ isteği atmaz.
 */
export default function TaskDetailDialog({ task, onHide }: Readonly<Props>) {
  const { t } = useTranslation()
  const { hasGrant } = useGrants()
  const subTaskBase = useHref('/minutes/subactions')
  const canOpenSubTask = hasGrant(GRANTS.SUBACTION_PAGE_VIEW)

  // Render sırasında state ayarlama (React'in "önceki prop'u sakla" kalıbı) — efekt gerekmez.
  const [shown, setShown] = useState<TaskDetailTarget | null>(task)
  if (task && task !== shown) {
    setShown(task)
  }

  const openSubTaskInNewTab = (id: number) => {
    window.open(`${subTaskBase}/${id}`, '_blank', 'noopener,noreferrer')
  }

  let body = null
  if (shown?.subTask) {
    body = <SubTaskDetailView actionId={shown.id} />
  } else if (shown) {
    body = (
      <MainTaskDetailView
        actionId={shown.id}
        onOpenSubTask={canOpenSubTask ? openSubTaskInNewTab : undefined}
      />
    )
  }

  return (
    <Dialog
      visible={task != null}
      onHide={onHide}
      header={t(
        shown?.subTask ? 'meetingManagement.detail.subTitle' : 'meetingManagement.detail.mainTitle',
      )}
      modal
      dismissableMask
      draggable={false}
      // Ana Görevi Gör pop-up'ıyla aynı kabuk: içerik uzun olabilir, kendi içinde kaydırılır.
      className="act-maintask-dialog"
      style={{ width: '60rem', maxWidth: '95vw' }}
    >
      {body}
    </Dialog>
  )
}
